<template>
  <div class="pb-24 max-w-3xl mx-auto">
    <header class="mb-6">
      <h1 class="text-2xl font-bold">Confirm Week {{ week || '—' }} Lines</h1>
      <p class="text-sm text-gray-400 mt-1">
        Read from the league email. Check anything flagged below against the
        picture before confirming.
      </p>
    </header>

    <div
      v-for="email in failedEmails"
      :key="email.id"
      class="mb-4 bg-red-900/40 border border-red-700 rounded-xl p-4 text-sm"
    >
      <div class="font-semibold text-red-300">
        An email for this week could not be processed
      </div>
      <div class="text-xs text-gray-400 mt-1 break-words">
        {{ email.subject || '(no subject)' }} · received {{ formatReceived(email.received_at) }}
      </div>
      <pre
        v-if="email.error"
        class="mt-2 whitespace-pre-wrap [overflow-wrap:anywhere] text-xs text-red-200/90 font-sans"
      >{{ email.error }}</pre>
      <a
        v-if="email.image_url"
        :href="email.image_url"
        target="_blank"
        rel="noopener"
        class="inline-flex items-center min-h-[40px] text-xs text-sky-300 underline"
      >
        Open the sheet image
      </a>
    </div>

    <div v-if="loading" class="text-gray-400 py-12 text-center">Loading lines…</div>

    <div v-else-if="error" class="bg-red-900/40 border border-red-700 rounded-xl p-4 text-sm">
      {{ error }}
    </div>

    <div v-else-if="!lines.length" class="text-gray-400 py-12 text-center">
      {{ failedEmails.length
        ? 'No lines could be read from this week’s email — see above.'
        : 'No lines have been received for this week yet.' }}
    </div>

    <template v-else>
      <div
        v-if="needsReview"
        class="mb-4 bg-amber-900/30 border border-amber-600/50 rounded-xl p-4 text-sm"
      >
        <span class="font-semibold text-amber-300">{{ needsReview }}</span>
        {{ needsReview === 1 ? 'game needs' : 'games need' }} a look — the email and
        ESPN disagree, or the game wasn't found on the schedule.
      </div>

      <div
        v-if="confirmed"
        class="mb-4 bg-emerald-900/30 border border-emerald-600/50 rounded-xl p-4 text-sm text-emerald-300"
      >
        This week is confirmed and frozen.
      </div>

      <ul class="space-y-3">
        <li
          v-for="line in lines"
          :key="line.id"
          class="bg-slate-800/50 backdrop-blur-sm border rounded-xl p-4"
          :class="line.reconciliation_status === 'agreed'
            ? 'border-slate-700/50'
            : 'border-amber-600/60'"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="font-medium">
                <span class="text-gray-400">{{ line.away_abbr }}</span>
                <span class="text-gray-600 mx-1">at</span>
                <span>{{ line.home_abbr }}</span>
              </div>
              <div class="text-sm text-gray-300 mt-1">
                <span class="font-semibold text-sky-300">{{ line.favorite }}</span>
                by {{ line.line }}
              </div>
              <div v-if="line.kickoff_at" class="text-xs text-gray-500 mt-1">
                {{ formatKickoff(line.kickoff_at) }}
              </div>
            </div>

            <div class="shrink-0 flex items-center gap-2">
              <button
                class="text-xs px-3 min-h-[40px] rounded-md bg-slate-700 hover:bg-slate-600 transition-colors"
                @click="toggleEdit(line)"
              >
                {{ editingId === line.id ? 'Cancel' : 'Edit' }}
              </button>
              <button
                class="h-10 w-10 flex items-center justify-center rounded-md text-gray-500 hover:text-red-300 hover:bg-red-900/30 transition-colors"
                :class="{ 'text-red-300 bg-red-900/30': confirmRemoveId === line.id }"
                :aria-label="`Remove the line for ${line.away_abbr} at ${line.home_abbr}`"
                @click="toggleRemove(line)"
              >
                <svg class="h-[18px] w-[18px]" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.02-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </button>
            </div>
          </div>

          <p
            v-if="line.reconciliation_status !== 'agreed' && confirmRemoveId !== line.id"
            class="mt-2 text-xs text-amber-300"
          >
            {{ statusMessage(line) }}
          </p>

          <div
            v-if="confirmRemoveId === line.id"
            class="mt-3 bg-red-900/30 border border-red-700/60 rounded-lg p-3 space-y-2"
          >
            <p class="text-xs text-gray-300">
              Remove the frozen line for {{ line.away_abbr }} at {{ line.home_abbr }}?
              The game stays on the schedule. This can't be undone.
            </p>
            <div class="flex gap-2">
              <button
                class="flex-1 min-h-[40px] rounded-md bg-red-600 hover:bg-red-500 text-xs font-medium transition-colors disabled:opacity-50"
                :disabled="removing"
                @click="remove(line)"
              >
                {{ removing ? 'Removing…' : 'Remove line' }}
              </button>
              <button
                class="flex-1 min-h-[40px] rounded-md bg-slate-700 hover:bg-slate-600 text-xs transition-colors"
                @click="confirmRemoveId = null"
              >
                Cancel
              </button>
            </div>
          </div>

          <div v-if="editingId === line.id" class="mt-4 pt-4 border-t border-slate-700 space-y-3">
            <label class="block text-xs text-gray-400">
              Favorite
              <select
                v-model="draft.favorite"
                class="mt-1 w-full min-h-[40px] bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-sm text-white"
              >
                <option :value="line.home_team">{{ line.home_team }}</option>
                <option :value="line.away_team">{{ line.away_team }}</option>
              </select>
            </label>

            <label class="block text-xs text-gray-400">
              Line (points the favorite gives)
              <input
                v-model="draft.line"
                type="number"
                step="0.5"
                min="0"
                inputmode="decimal"
                class="mt-1 w-full min-h-[40px] bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-sm text-white"
              />
            </label>

            <button
              class="w-full min-h-[40px] rounded-md bg-emerald-600 hover:bg-emerald-500 text-sm font-medium transition-colors disabled:opacity-50"
              :disabled="saving"
              @click="save(line)"
            >
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </li>
      </ul>

      <button
        class="mt-6 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 font-semibold transition-colors disabled:opacity-50"
        :disabled="confirming || confirmed"
        @click="confirmAll"
      >
        {{ confirmed ? 'Confirmed' : confirming ? 'Confirming…' : `Confirm all ${lines.length} lines` }}
      </button>
    </template>

    <section v-if="pendingReports.length" class="mt-10 border-t border-slate-800 pt-6">
      <h2 class="text-lg font-bold">Pool reports awaiting review</h2>
      <p class="text-sm text-gray-400 mt-1">
        The commissioner's weekly PDFs. Check the sheet, then confirm to make its
        rows the ones the standings and rank chart read.
      </p>

      <ul class="mt-4 space-y-3">
        <li
          v-for="report in pendingReports"
          :key="report.id"
          class="bg-slate-800/50 border rounded-xl p-4 text-sm"
          :class="report.warnings.length ? 'border-amber-600/60' : 'border-slate-700/50'"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="font-medium capitalize">
                {{ report.kind }} · {{ report.season }} Week {{ report.week }}
              </div>
              <div class="text-xs text-gray-500 mt-1">
                {{ report.row_count }} players · received {{ formatReceived(report.received_at) }}
              </div>
              <a
                v-if="report.document_url"
                :href="report.document_url"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center min-h-[40px] text-xs text-sky-300 underline"
              >
                Open the PDF
              </a>
            </div>
            <button
              class="shrink-0 text-xs px-3 min-h-[40px] rounded-md bg-emerald-600 hover:bg-emerald-500 transition-colors disabled:opacity-50"
              :disabled="confirmingReportId === report.id"
              @click="confirmReport(report)"
            >
              {{ confirmingReportId === report.id ? 'Confirming…' : 'Confirm' }}
            </button>
          </div>
          <ul v-if="report.warnings.length" class="mt-2 text-xs text-amber-300 list-disc pl-4">
            <li v-for="(w, i) in report.warnings" :key="i">{{ w }}</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import api from '@/utils/api';

export default {
  name: 'ConfirmLines',
  data() {
    return {
      lines: [],
      failedEmails: [],
      week: null,
      season: null,
      needsReview: 0,
      confirmed: false,
      loading: true,
      error: null,
      editingId: null,
      draft: { favorite: '', line: 0 },
      saving: false,
      removing: false,
      confirmRemoveId: null,
      confirming: false,
      poolReports: [],
      confirmingReportId: null,
    };
  },
  computed: {
    pendingReports() {
      return this.poolReports.filter((report) => report.status === 'parsed');
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      // Independent of the line load: a league_lines failure must not hide
      // the reason there are no lines to show.
      this.loadFailedEmails();
      this.loadPoolReports();
      try {
        const { data } = await api.get('/api/v1/league_lines');
        this.lines = data.lines;
        this.week = data.week;
        this.season = data.season;
        this.needsReview = data.needs_review;
        this.confirmed = data.confirmed;
      } catch (e) {
        this.error = 'Could not load this week’s lines.';
      } finally {
        this.loading = false;
      }
    },
    // Emails that parsed as line sheets but reconciled to nothing on the
    // schedule land here rather than showing up as an empty confirm screen.
    async loadFailedEmails() {
      try {
        const { data } = await api.get('/api/v1/line_emails');
        this.failedEmails = (data.line_emails || []).filter(
          (email) => email.status === 'failed'
        );
      } catch (e) {
        this.failedEmails = [];
      }
    },
    async loadPoolReports() {
      try {
        const { data } = await api.get('/api/v1/pool_reports');
        this.poolReports = data.pool_reports || [];
      } catch (e) {
        this.poolReports = [];
      }
    },
    async confirmReport(report) {
      this.confirmingReportId = report.id;
      try {
        await api.post(`/api/v1/pool_reports/${report.id}/confirm`);
        await this.loadPoolReports();
      } catch (e) {
        this.error = 'Could not confirm that report.';
      } finally {
        this.confirmingReportId = null;
      }
    },
    statusMessage(line) {
      if (line.reconciliation_status === 'disagreed') {
        return 'The email’s capitalization disagreed with ESPN about who is home. ESPN was used.';
      }
      if (line.reconciliation_status === 'unmatched') {
        return 'This game was not found on ESPN’s schedule, so home field came from the email alone.';
      }
      return '';
    },
    toggleEdit(line) {
      this.confirmRemoveId = null;
      if (this.editingId === line.id) {
        this.editingId = null;
        return;
      }
      this.editingId = line.id;
      this.draft = { favorite: line.favorite, line: line.line };
    },
    toggleRemove(line) {
      this.confirmRemoveId = this.confirmRemoveId === line.id ? null : line.id;
      if (this.confirmRemoveId) this.editingId = null;
    },
    async remove(line) {
      this.removing = true;
      try {
        await api.delete(`/api/v1/league_lines/${line.id}`);
        this.confirmRemoveId = null;
        this.editingId = null;
        await this.load();
      } catch (e) {
        this.error = 'Could not remove that line.';
      } finally {
        this.removing = false;
      }
    },
    async save(line) {
      this.saving = true;
      try {
        await api.patch(`/api/v1/league_lines/${line.id}`, {
          favorite: this.draft.favorite,
          line: this.draft.line,
        });
        this.editingId = null;
        await this.load();
      } catch (e) {
        this.error = 'Could not save that change.';
      } finally {
        this.saving = false;
      }
    },
    async confirmAll() {
      this.confirming = true;
      try {
        await api.post('/api/v1/league_lines/confirm', {
          season: this.season,
          week: this.week,
        });
        await this.load();
      } catch (e) {
        this.error = 'Could not confirm the week.';
      } finally {
        this.confirming = false;
      }
    },
    formatReceived(value) {
      if (!value) return 'unknown time';
      return new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    formatKickoff(value) {
      const date = new Date(value);
      return date.toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
  },
};
</script>
